import { AuthServiceInterface } from '@mint-lib/api';
import type { IFlagsmithTrait } from '@mint-lib/flags';
import { EventEmitter } from '@mint-lib/service-locator';

import {
  FeaturePermission,
  PERMISSIONS_HIERARCHY,
} from '../constants/index.js';
import { PermissionsRequestType } from '../getUserProfile/getUserProfile.types.js';
import { AmbiguousUserProfile } from '../ProfileContext/index.jsx';

export type ProfileEvents = {
  update: [profile: AmbiguousUserProfile];
};

export class ProfileService {
  private readonly eventEmitter: EventEmitter<ProfileEvents> =
    new EventEmitter();
  private profileData: AmbiguousUserProfile = null;
  private customTraitsObj: Record<string, boolean | null> | null = null;
  private readonly eventListeners: Set<() => void> = new Set();
  constructor(private readonly auth: AuthServiceInterface) {}

  subscribe(cb: () => void) {
    this.eventListeners.add(cb);
    return () => {
      this.eventListeners.delete(cb);
    };
  }

  setProfileData(
    data:
      | AmbiguousUserProfile
      | ((user: AmbiguousUserProfile) => AmbiguousUserProfile),
  ) {
    if (typeof data === 'function') {
      this.profileData = data(this.profileData);
    } else {
      this.profileData = data;
    }
    this.eventListeners.forEach((cb) => cb());
    return this.eventEmitter.emit('update', this.profileData);
  }

  getAgencyWorkspaceId() {
    return this.profileData?.profile?.company?.agencyWorkspaceId;
  }

  getEventEmitter() {
    return this.eventEmitter;
  }

  getProfileData() {
    return this.profileData;
  }

  getPermissions() {
    return this.profileData?.profile?.permissions ?? {};
  }

  hasPermission(requestedAccess: PermissionsRequestType) {
    if (!this.profileData) {
      return false;
    }
    /**
     * Retrieves the user's permissions.
     * @returns A non-null object containing the user's permissions.
     */
    const permissions = this.getPermissions();
    const [item, requestedAction] = requestedAccess.split(':') as [
      keyof typeof permissions,
      FeaturePermission,
    ];
    const requestedAccessIndex = PERMISSIONS_HIERARCHY.indexOf(requestedAction);
    const requiredActionIndex = PERMISSIONS_HIERARCHY.indexOf(
      permissions[item] as FeaturePermission,
    );

    // Safecheck: if no permission found consider denial
    if (requestedAccessIndex === -1 || requiredActionIndex === -1) {
      return false;
    }

    return requiredActionIndex >= requestedAccessIndex;
  }

  isAgencyCompany() {
    return this.profileData?.profile?.company?.agencyCompany ?? false;
  }

  isRedmillUser() {
    return this.profileData?.profile?.config?.isRedmill ?? false;
  }

  isControllerCompany() {
    return this.profileData?.profile?.company?.controllerCompany ?? false;
  }

  isIdpProvider() {
    return this.profileData?.profile?.company?.idpEnabled ?? false;
  }

  isRetailLicensee() {
    return this.profileData?.profile?.company?.license === 'RETAIL';
  }

  getRelease() {
    return this.profileData?.release ?? null;
  }

  getCustomTraits() {
    return this.profileData?.customTraits ?? null;
  }

  getCustomTraitsObj() {
    return this.customTraitsObj ?? this.getInitialCustomTraitsAsObject();
  }

  getInitialCustomTraitsAsObject() {
    const traitsArray = this.getCustomTraits();

    if (!traitsArray || traitsArray.length === 0) {
      return {};
    }

    return traitsArray.reduce(
      (acc, trait) => {
        acc[trait] = { value: true, transient: true };
        return acc;
      },
      {} as Record<string, IFlagsmithTrait>,
    );
  }

  setCustomTraits(data: Record<string, boolean | null>) {
    this.customTraitsObj = data;
    this.eventListeners.forEach((cb) => cb());

    return this.eventEmitter.emit('update', this.profileData);
  }
}
